import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ApiMethods from "../../../../ApiManager/ApiMethods";
import { fetchClientBookings } from "../../../data/clientBookingsStore";
import { upsertClientPackageInCache, removeClientPackageFromCache } from "../../../data/membersStore";
import useLockBodyScroll from "../../../hooks/useLockBodyScroll";
import { useAlert } from "../../../context/AlertContext";
import DeletePopUp from "./DeletePopUp";
import ClientAvatar from "./ClientAvatar";
import { colorForName } from "../../../../common/utils/colorForName";
import PackageCubeIcon from "./icons/PackageCubeIcon";
import "./packageFlow.css";

const useIsDesktopTablet = () => {
  const [isDesktopTablet, setIsDesktopTablet] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 768 : false
  );
  useEffect(() => {
    const onResize = () => setIsDesktopTablet(window.innerWidth >= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isDesktopTablet;
};

export const cycleStats = (pkg) => {
  const total = pkg.total_sessions ?? pkg.package_service_qty ?? 0;
  const used = Math.min(pkg.used ?? 0, total || Infinity);
  const remaining = Math.max(0, total - used);
  const pct = total > 0 ? Math.round((used / total) * 100) : 0;
  const isExpiredByDate = pkg.end_date && moment(pkg.end_date).isBefore(moment(), "day");
  // No `total > 0` guard: a 0-qty package (e.g. the legacy-backfill placeholder) has 0 used
  // >= 0 total immediately, so it reads as used-up rather than perpetually "active".
  const isUsedUp = used >= total;
  // A client-requested package sits at is_requested=true until staff reviews it; one they
  // rejected keeps is_requested=false but never got is_active=true (see
  // AuthController.getClientPackageCycles, which is_active also gates for actual booking
  // eligibility) — neither can ever read as "Aktivan" just because it's unused/unexpired.
  const isPending = !!pkg.is_requested;
  const isRejected = !isPending && !pkg.is_active;
  let statusLabel = "Aktivan";
  if (isPending) statusLabel = "Na čekanju";
  else if (isRejected) statusLabel = "Odbijen";
  else if (isUsedUp) statusLabel = "Iskorišćen";
  else if (isExpiredByDate) statusLabel = "Istekao";
  const isActive = !isPending && !isRejected && !isUsedUp && !isExpiredByDate;
  return {
    total,
    used,
    remaining,
    pct,
    statusLabel,
    isActive,
    isPending,
    isRejected,
    isUsedUp,
    isExpiredByDate,
  };
};

// Badge label/icon/tone per package status — pending's "Na odobravanju" and active's "Odobren"
// are deliberately different wording from cycleStats' own statusLabel ("Na čekanju"/"Aktivan",
// still used elsewhere), matching this card's own reference design; rejected/used-up/expired
// keep cycleStats' existing wording since it already matches.
export const STATUS_META = {
  pending: { label: "Na odobravanju", tone: "pending", Icon: AccessTimeOutlinedIcon },
  active: { label: "Odobren", tone: "active", Icon: CheckCircleOutlineIcon },
  rejected: { label: "Odbijen", tone: "rejected", Icon: CancelOutlinedIcon },
  usedUp: { label: "Iskorišćen", tone: "neutral", Icon: null },
  expired: { label: "Istekao", tone: "neutral", Icon: null },
};
export function statusKeyFor(pkg) {
  const s = cycleStats(pkg);
  if (s.isPending) return "pending";
  if (s.isRejected) return "rejected";
  if (s.isActive) return "active";
  if (s.isUsedUp) return "usedUp";
  return "expired";
}

// A cycle nobody's booked into yet (every pending/just-approved request included) has no
// start_date/end_date — view_clients_packages_full only derives those from real bookings — so
// validity reads from the request's own snapshot (duration_days) until a real end_date exists.
export function validityText(pkg) {
  if (pkg.end_date) return `Važi do ${moment(pkg.end_date).format("D. MMM YYYY.")}`;
  const days = pkg.duration_days ?? pkg.package_period_days;
  if (days) return `Važi ${days} dana od prvog treninga`;
  return "Bez ograničenja trajanja";
}
// The one package card used everywhere a cycle is shown — the summary view's current-package
// slot, the history list, and the history-detail header (pending review included). Replaces
// the old two-look split (a plain CycleCard for lists/pending, a blue gradient ring
// HeroPackageCard matching the User App's own card for the current package) with a single flat,
// info-strip design per the office's own reference — the two apps' package cards are now
// deliberately allowed to diverge visually, since staff-only fields (price, payment status)
// don't apply to the member-facing card at all.
// The "⋮" affordance top-right stays a plain decorative <span> (never fighting with the
// clickable header region below) unless `onDeleteRequest` is passed — only the history-detail
// card (the one real, already-decided package a "Vidi detalje" click lands on) wires that up,
// so every other place this card renders (pending review, the placeholder) keeps the exact
// same non-interactive "⋮" as before.
// `onTogglePaid(pkg, nextPaid)` makes the Nije plaćeno/Plaćeno pills real, saving immediately
// (independent of any Approve/Reject action) — omit it to render them inert.
const PackageStatusCard = ({
  pkg,
  onClick,
  onTogglePaid,
  saving,
  placeholder,
  showProgress,
  showDates,
  onDeleteRequest,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocMouseDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  if (placeholder) {
    return (
      <div className="hf-pkg-status-card hf-pkg-status-card-placeholder">
        <div className="hf-pkg-status-top">
          <span className="hf-pkg-status-icon">
            <EditOutlinedIcon sx={{ fontSize: 16 }} />
          </span>
          <div className="hf-pkg-status-info">
            <div className="hf-pkg-status-label">PAKET</div>
            <div className="hf-pkg-status-name">Nema aktivnog paketa</div>
            <div className="hf-pkg-status-validity">Dodeljeni paket će biti prikazan ovde.</div>
          </div>
        </div>
      </div>
    );
  }

  const stats = cycleStats(pkg);
  const statusKey = statusKeyFor(pkg);
  const meta = STATUS_META[statusKey];
  const StatusIcon = meta.Icon;
  const priceValue = pkg.price ?? pkg.package_price ?? 0;
  const isPaid = !!pkg.payment_date;

  // A pending request has no payment decision to show or make yet — that only becomes
  // relevant once staff actually approves it (Odobri paket), so "Status plaćanja" is hidden
  // entirely while pending rather than showing a premature/misleading paid-or-not read. With
  // no toggle buttons left inside the card in that state, the WHOLE card can safely become the
  // single clickable target (no nested-button conflict) — every other status still needs the
  // two payment buttons inside, so those keep the header-only clickable region.
  const isPendingCard = statusKey === "pending";
  const showPayment = !isPendingCard;
  const wholeCardClickable = !!onClick && isPendingCard;
  const OuterTag = wholeCardClickable ? "button" : "div";
  const TopTag = !wholeCardClickable && onClick ? "button" : "div";

  // Pending/active cards are tinted by PAYMENT status (green once paid, red until then) — the
  // card wash, the "Status plaćanja" label, and the active toggle pill all share that one
  // color, per the office's own reference. A pending request (no payment shown at all) and a
  // terminal cycle (odbijen/iskorišćen/istekao) both keep their own fixed approval-status tone
  // instead — payment isn't the signal yet, or isn't relevant anymore.
  const isTerminal = statusKey === "rejected" || statusKey === "usedUp" || statusKey === "expired";
  const toneClass = isTerminal || isPendingCard ? meta.tone : isPaid ? "paid" : "unpaid";
  // The badge itself stays tied to APPROVAL status only, never payment — "Odobren" must always
  // read as a positive green badge, "Na odobravanju" always blue, regardless of whether the
  // package has been marked paid yet (an unpaid-but-approved package showing a red "Odobren"
  // badge reads as a contradiction: the text says approved, the color says the opposite).
  const badgeToneClass = meta.tone;

  // Once a package's own end date has passed, there's nothing left to toggle back and forth —
  // "Nije plaćeno" is always inert past that point (re-confirming "still unpaid" changes
  // nothing). "Plaćeno" stays clickable ONLY while still unpaid, though, so staff can still
  // log a late/forgotten payment after the fact; once it's actually marked paid there's no
  // reason to undo that either, so both lock at that point.
  const isExpired = statusKey === "expired";
  const unpaidBtnDisabled = !onTogglePaid || saving || isExpired;
  const paidBtnDisabled = !onTogglePaid || saving || (isExpired && isPaid);

  return (
    <OuterTag
      type={wholeCardClickable ? "button" : undefined}
      className={`hf-pkg-status-card hf-pkg-status-card-${toneClass}${
        wholeCardClickable ? " hf-pkg-status-card-clickable" : ""
      }`}
      onClick={wholeCardClickable ? onClick : undefined}
    >
      <TopTag
        type={!wholeCardClickable && onClick ? "button" : undefined}
        className={`hf-pkg-status-top${
          !wholeCardClickable && onClick ? " hf-pkg-status-top-clickable" : ""
        }`}
        onClick={!wholeCardClickable ? onClick : undefined}
      >
        <span className="hf-pkg-status-icon">
          <FitnessCenterIcon sx={{ fontSize: 18 }} />
        </span>
        <div className="hf-pkg-status-info">
          <div className="hf-pkg-status-label">PAKET</div>
          <div className="hf-pkg-status-name">{pkg.package_name}</div>
          <div className="hf-pkg-status-validity">{validityText(pkg)}</div>
        </div>
        <div className="hf-pkg-status-actions">
          <span className={`hf-pkg-status-badge hf-pkg-status-badge-${badgeToneClass}`}>
            {StatusIcon && <StatusIcon sx={{ fontSize: 13 }} />}
            {meta.label}
          </span>
          {onDeleteRequest ? (
            <div className="hf-pkg-status-menu-wrap" ref={menuRef}>
              <button
                type="button"
                className="hf-pkg-status-menu hf-pkg-status-menu-btn"
                aria-haspopup="true"
                aria-expanded={menuOpen}
                aria-label="Opcije paketa"
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen((v) => !v);
                }}
              >
                <MoreVertIcon sx={{ fontSize: 18 }} />
              </button>
              {menuOpen && (
                <div className="hf-pkg-status-menu-dropdown" role="menu">
                  <button
                    type="button"
                    className="hf-pkg-status-menu-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuOpen(false);
                      onDeleteRequest(pkg);
                    }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    Obriši paket
                  </button>
                </div>
              )}
            </div>
          ) : (
            <span className="hf-pkg-status-menu" aria-hidden="true">
              <MoreVertIcon sx={{ fontSize: 18 }} />
            </span>
          )}
        </div>
      </TopTag>

      {/* Opt-in (history-detail only, via `showProgress`) — the pending-request card in the
          summary view reuses this same component without it, since "% progress" isn't
          meaningful before a request is even approved. */}
      {showProgress && (
        <div
          className="hf-pkg-progress-track hf-pkg-status-progress"
        >
          <div
            className={`hf-pkg-progress-fill ${
              stats.pct >= 100 ? "hf-pkg-progress-full" : stats.pct >= 75 ? "hf-pkg-progress-warn" : ""
            }`}
            style={{ width: `${Math.min(100, stats.pct)}%` }}
          />
        </div>
      )}

      {/* Opt-in (history-detail only, via `showDates`) — moved in here from a standalone block
          below the card so it sits "in the card, where there's room" instead of floating
          underneath it as its own separate element. */}
      {showDates && pkg.start_date && (
        <div className="hf-pkg-req-meta hf-pkg-status-meta">
          <div className="hf-pkg-req-meta-col">
            <div className="hf-pkg-req-meta-label">
              <EventOutlinedIcon sx={{ fontSize: 14 }} />
              Počinje
            </div>
            <div className="hf-pkg-req-meta-value">{moment(pkg.start_date).format("DD.MM.YYYY.")}</div>
          </div>
          <div className="hf-pkg-req-meta-divider" />
          <div className="hf-pkg-req-meta-col">
            <div className="hf-pkg-req-meta-label">
              <EventOutlinedIcon sx={{ fontSize: 14 }} />
              Ističe
            </div>
            <div className="hf-pkg-req-meta-value">
              {pkg.end_date ? moment(pkg.end_date).format("DD.MM.YYYY.") : "—"}
            </div>
          </div>
        </div>
      )}

      <div className="hf-pkg-req-meta hf-pkg-status-meta">
        <div className="hf-pkg-req-meta-col">
          <div className="hf-pkg-req-meta-label">
            <CreditCardOutlinedIcon sx={{ fontSize: 14 }} />
            Cena paketa
          </div>
          <div className="hf-pkg-req-meta-value">{Number(priceValue).toLocaleString("sr-RS")} RSD</div>
        </div>
        <div className="hf-pkg-req-meta-divider" />
        <div className="hf-pkg-req-meta-col">
          <div className="hf-pkg-req-meta-label">
            <FitnessCenterIcon sx={{ fontSize: 14 }} />
            Ukupno termina
          </div>
          <div className="hf-pkg-req-meta-value">{stats.total}</div>
        </div>
        <div className="hf-pkg-req-meta-divider" />
        <div className="hf-pkg-req-meta-col">
          <div className="hf-pkg-req-meta-label">
            <EventOutlinedIcon sx={{ fontSize: 14 }} />
            Preostalo termina
          </div>
          <div className="hf-pkg-req-meta-value">{stats.remaining}</div>
        </div>
      </div>

      {showPayment && (
        <div className={`hf-pkg-req-section hf-pkg-status-pay hf-pkg-status-pay-${toneClass}`}>
          <div className="hf-pkg-req-section-label">
            Status plaćanja
            <InfoOutlinedIcon sx={{ fontSize: 14 }} />
          </div>
          <div className="hf-pkg-paytoggle">
            <button
              type="button"
              className={`hf-pkg-paytoggle-btn hf-pkg-paytoggle-btn-unpaid${
                !isPaid ? " hf-pkg-paytoggle-btn-active" : ""
              }`}
              disabled={unpaidBtnDisabled}
              onClick={() => onTogglePaid?.(pkg, false)}
            >
              <CancelOutlinedIcon sx={{ fontSize: 15 }} />
              Nije plaćeno
            </button>
            <button
              type="button"
              className={`hf-pkg-paytoggle-btn hf-pkg-paytoggle-btn-paid${
                isPaid ? " hf-pkg-paytoggle-btn-active" : ""
              }`}
              disabled={paidBtnDisabled}
              onClick={() => onTogglePaid?.(pkg, true)}
            >
              <CheckCircleOutlineIcon sx={{ fontSize: 15 }} />
              Plaćeno
            </button>
          </div>
        </div>
      )}
    </OuterTag>
  );
};

// Compact linear progress gauge for a "Trenutni paket" row (PackageSummaryRow below) — the
// one bucket where "how far through the cycle" is actually the most useful number at a
// glance; future/past rows show a plain text stat instead since there's nothing to visualize.
// Was a circular ring — switched to the same linear bar already used up in PackageStatusCard
// (.hf-pkg-progress-track/.hf-pkg-progress-fill) since the ring's fixed 76px height forced its
// own full-width row on Mobile (see the row's `flex-wrap` below), costing far more vertical
// space than a thin bar needs on every platform, not just Mobile.
const PackageProgressBar = ({ used, total, pct }) => (
  <div className="hf-pkg-row-bar">
    <div className="hf-pkg-row-bar-label">
      <span className="hf-pkg-row-bar-count">
        {used}/{total}
      </span>
      <span className="hf-pkg-row-bar-caption">iskorišćeno</span>
    </div>
    <div className="hf-pkg-progress-track hf-pkg-row-bar-track">
      <div
        className={`hf-pkg-progress-fill ${
          pct >= 100 ? "hf-pkg-progress-full" : pct >= 75 ? "hf-pkg-progress-warn" : ""
        }`}
        style={{ width: `${Math.min(100, pct)}%` }}
      />
    </div>
  </div>
);

// One row in the "Paket člana" summary's bucketed lists (Budući/Trenutni/Prošli) — a compact,
// read-only horizontal summary; the actual paid/unpaid TOGGLE and the note/approve-reject
// actions only live behind "Vidi detalje" (PackageStatusCard, in history-detail), so this row
// never needs onTogglePaid at all. "Budući" = approved but nobody's booked into it yet (no
// start_date); "Trenutni" = approved and already has bookings; anything else here is "Prošli"
// (rejected, used up, or past its own end date) — the three are mutually exclusive and, unlike
// the pending request, never need review actions.
// The current package's own card — the gradient "hero" look the office used to have just for
// this one bucket, before everything was unified into a single flat PackageSummaryRow (see
// that comment above). Budući/Prošli stay on the plain flat row below; this is the one place
// "how far through the cycle" and "when does it expire" are actually the headline, so it gets
// the visual weight. Approved via a preview mockup (gradient card, linear bar instead of the
// old ring, real --hf-primary blue) before landing here.
const CurrentPackageHeroCard = ({ pkg, stats, isPaid, onViewDetails }) => (
  <div className="hf-pkg-row hf-pkg-row-hero">
    <div className="hf-pkg-hero-top">
      <div className="hf-pkg-hero-label">
        <PackageCubeIcon sx={{ fontSize: 15 }} />
        {pkg.package_name}
      </div>
      <span
        className={`hf-pkg-hero-pay${isPaid ? " hf-pkg-hero-pay-paid" : " hf-pkg-hero-pay-unpaid"}`}
      >
        {isPaid ? <CheckCircleOutlineIcon sx={{ fontSize: 15 }} /> : <CancelOutlinedIcon sx={{ fontSize: 15 }} />}
        {isPaid ? "Plaćeno" : "Nije plaćeno"}
      </span>
    </div>

    <div className="hf-pkg-hero-usage">
      <div className="hf-pkg-hero-usage-top">
        <span className="hf-pkg-hero-usage-count">
          {stats.used}/{stats.total}
        </span>
        <span className="hf-pkg-hero-usage-caption">iskorišćeno</span>
      </div>
      <div className="hf-pkg-hero-track">
        <div className="hf-pkg-hero-fill" style={{ width: `${Math.min(100, stats.pct)}%` }} />
      </div>
    </div>

    <div className="hf-pkg-hero-divider" />

    <div className="hf-pkg-hero-footer">
      <div className="hf-pkg-hero-validity">
        <EventOutlinedIcon sx={{ fontSize: 16 }} className="hf-pkg-hero-validity-icon" />
        <div className="hf-pkg-hero-validity-text">
          <div className="hf-pkg-hero-validity-main">{validityText(pkg)}</div>
          <div className="hf-pkg-hero-validity-sub">
            Preostale sesije možete zakazati do ovog datuma.
          </div>
        </div>
      </div>
      <button type="button" className="hf-pkg-hero-details" onClick={onViewDetails}>
        Vidi detalje
        <ChevronRightIcon sx={{ fontSize: 16 }} />
      </button>
    </div>
  </div>
);

const PackageSummaryRow = ({ pkg, onViewDetails }) => {
  const stats = cycleStats(pkg);
  const isPaid = !!pkg.payment_date;
  // Approved (isActive) is "Trenutni" the moment it happens now, not once the member's also
  // gotten around to booking a first session — see futureCycles/currentCycles above. Anything
  // reaching the flat row below is Prošli (rejected/used up/expired) — never "Budući" anymore.
  const isCurrent = stats.isActive;

  if (isCurrent) {
    return (
      <CurrentPackageHeroCard
        pkg={pkg}
        stats={stats}
        isPaid={isPaid}
        onViewDetails={onViewDetails}
      />
    );
  }

  return (
    <div className="hf-pkg-row hf-pkg-row-past">
      <span className="hf-pkg-status-icon hf-pkg-row-icon">
        <FitnessCenterIcon sx={{ fontSize: 18 }} />
      </span>
      <div className="hf-pkg-row-info">
        <div className="hf-pkg-status-label">PAKET</div>
        <div className="hf-pkg-row-name">{pkg.package_name}</div>
        <div className="hf-pkg-status-validity">{validityText(pkg)}</div>
      </div>

      {/* Everything reaching here is Prošli — Trenutni returned its own hero card above
          already, and Budući no longer exists as its own bucket. */}
      <div className="hf-pkg-row-stat">
        <div className="hf-pkg-row-stat-col">
          <div className="hf-pkg-row-stat-label">
            <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
            {stats.isRejected ? "Odbijen" : "Završen"}
          </div>
          <div className="hf-pkg-row-stat-sub">
            {stats.isRejected ? "Zahtev je odbijen" : `Iskorišćeno ${stats.used}/${stats.total}`}
          </div>
        </div>
      </div>

      <div className={`hf-pkg-row-pay${isPaid ? " hf-pkg-row-pay-paid" : " hf-pkg-row-pay-unpaid"}`}>
        {isPaid ? <CheckCircleOutlineIcon sx={{ fontSize: 15 }} /> : <CancelOutlinedIcon sx={{ fontSize: 15 }} />}
        {isPaid ? "Plaćeno" : "Nije plaćeno"}
      </div>

      <button type="button" className="hf-pkg-row-details" onClick={onViewDetails}>
        Vidi detalje
        <ChevronRightIcon sx={{ fontSize: 16 }} />
      </button>
    </div>
  );
};

// A bucket's section head (title + count) + description + its rows — renders nothing at all
// once the bucket is empty, so an untouched member (no future/past packages) doesn't show
// three empty headings. Collapsed to just the most recent (first — `sorted` is already
// created_at-desc) row by default when there's more than one, with its own "Prikaži sve"
// toggle to reveal the rest — a member with a long "Prošli paketi" history shouldn't push
// everything else on the summary screen down by default.
const PackageSection = ({ title, description, cycles, onOpen }) => {
  const [expanded, setExpanded] = useState(false);
  if (cycles.length === 0) return null;
  const visible = expanded ? cycles : cycles.slice(0, 1);
  return (
    <>
      <div className="hf-pkg-section-head">
        <span className="hf-pkg-section-title">{title}</span>
        <span className="hf-pkg-section-count">{cycles.length}</span>
      </div>
      <div className="hf-pkg-section-desc">{description}</div>
      {/* No inner scroll cap here — the modal's own body already scrolls as a whole, so
          capping this list's own height too (a prior attempt) just created a second, nested
          scrollbar that kicked in before the outer one had actually run out of room, and
          squeezed rows short on Mobile in the process. Cards just flow naturally now. */}
      <div className="hf-pkg-row-list">
        {visible.map((pkg) => (
          <PackageSummaryRow key={pkg.id} pkg={pkg} onViewDetails={() => onOpen(pkg)} />
        ))}
      </div>
      {cycles.length > 1 && (
        <button
          type="button"
          className="hf-pkg-section-toggle"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Prikaži samo poslednji" : `Prikaži sve (${cycles.length})`}
        </button>
      )}
    </>
  );
};

// One row in the "Istorija termina" table (history-detail, once "Prikaži sve" expands it) —
// replaces the old one-card-per-session list, which took a full card's worth of height per
// training and pushed the popup's real length way past what a few sessions actually need.
const SessionTableRow = ({ index, session }) => {
  const start = moment(session.start);
  const isPast = start.isBefore(moment());
  const isNoShow = session.status === "noshow";
  const label = isNoShow ? "Nije se pojavio/la" : isPast ? "Odrađeno" : "Zakazano";
  const badgeClass = isNoShow
    ? "hf-pkg-session-badge-danger"
    : isPast
      ? "hf-pkg-session-badge-done"
      : "hf-pkg-session-badge-upcoming";
  return (
    <tr className="hf-pkg-session-tr">
      <td className="hf-pkg-session-td hf-pkg-session-td-index">{index}</td>
      <td className="hf-pkg-session-td">{start.format("DD.MM.YYYY.")}</td>
      <td className="hf-pkg-session-td">{start.format("HH:mm")}</td>
      <td className="hf-pkg-session-td">
        <span className={`hf-pkg-session-badge ${badgeClass}`}>
          {!isNoShow && <CheckCircleOutlineIcon sx={{ fontSize: 13 }} />}
          {label}
        </span>
      </td>
    </tr>
  );
};

// Two internal views inside the one popup instance — "summary" (default landing view: Add
// Package, the pending request if any, then every cycle bucketed into Budući/Trenutni/Prošli)
// and "history-detail" (one cycle's full stats + its completed-session list, reached via a
// row's own "Vidi detalje" — mirrors "Istorija paketa"/pending review). Kept as one
// component/one popup instance (not two separate modals) so the same overlay/header/close
// button and body-scroll-lock apply everywhere, matching how PackageAssignFlow's own
// select→confirm steps already work in this app.
const PackageDetailsModal = ({
  open,
  member,
  packages,
  onClose,
  onAssignNew,
  onChangePackage,
  initialCycleId,
  // Optional — fires right after `handleTogglePaid` (below) successfully saves, with the
  // updated package row and the `nextPaid` value that was just set. Every existing caller
  // simply doesn't pass this, so their behavior is completely unchanged; a caller that wants
  // to react to a payment change (e.g. close itself once a package is marked paid) can.
  onPaymentToggled,
}) => {
  const isDesktopTablet = useIsDesktopTablet();
  const { showAlert } = useAlert();
  // Always mounted (returns null when !open rather than being conditionally rendered by its
  // parent), so the lock itself must be gated on `open`, not just mount.
  useLockBodyScroll(open);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [view, setView] = useState("summary");
  const [selectedCycleId, setSelectedCycleId] = useState(null);
  const [cycleSessions, setCycleSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  // "Istorija termina" collapses to just a summary line (used/remaining count) by default —
  // the full table only renders once "Prikaži sve" is clicked.
  const [sessionsExpanded, setSessionsExpanded] = useState(false);
  const [reviewSaving, setReviewSaving] = useState(false);
  // The Nije plaćeno/Plaćeno toggle (PackageStatusCard) saves immediately on click, for any
  // package regardless of status — id of whichever card's toggle is currently in flight, so
  // only that one card's buttons disable rather than every card on screen.
  const [savingPaidId, setSavingPaidId] = useState(null);
  // "Plaćeno" click (PackageStatusCard) opens this price-confirm popup instead of saving
  // immediately — same "Potvrdite cenu" step PackageAssignFlow's own "Dodeli i plati" already
  // uses, so a discount/price change can be applied right when payment is actually confirmed,
  // not just at assignment time. Holds the package being confirmed, or null; "Nije plaćeno"
  // still saves straight away — there's no price to confirm when un-marking a payment.
  const [payConfirmTarget, setPayConfirmTarget] = useState(null);
  const [payConfirmPrice, setPayConfirmPrice] = useState("");
  // "Obriši paket" (history-detail card's "⋮" menu) — the package awaiting delete
  // confirmation, or null. Holds the whole cycle object so the confirm popup and the actual
  // delete call both have it without needing a second lookup.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingPackage, setDeletingPackage] = useState(false);

  const memberClientId = member?.id != null ? member.id : member?.client_id;

  // A fresh view every time the popup is opened for a (possibly different) member — otherwise
  // closing from "history-detail" and reopening on another member would still show that view.
  // `initialCycleId` (PackageManagement's own "Pregledaj paket" row action) skips the summary
  // entirely and jumps straight to that one package's detail — the row that opened this
  // already told us exactly which package, so the full Budući/Trenutni/Prošli list would just
  // be an extra click back to where the trainer already was. "Nazad" still reaches summary
  // from there same as always; this only changes where the popup *lands* on open.
  useEffect(() => {
    if (initialCycleId != null) {
      setView("history-detail");
      setSelectedCycleId(initialCycleId);
    } else {
      setView("summary");
      setSelectedCycleId(null);
    }
    setSessionsExpanded(false);
  }, [open, memberClientId, initialCycleId]);

  useEffect(() => {
    if (!isDesktopTablet || !open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isDesktopTablet, open, onClose]);

  useEffect(() => {
    if (!open || !memberClientId) {
      setUpcomingBookings([]);
      return;
    }
    let cancelled = false;
    fetchClientBookings(memberClientId)
      .then((res) => {
        if (cancelled) return;
        const now = moment();
        const upcoming = (res || [])
          .filter((b) => b.status === "book" && moment(b.start).isAfter(now))
          .sort((a, b) => moment(a.start).diff(moment(b.start)))
          .slice(0, 5);
        setUpcomingBookings(upcoming);
      })
      .catch((err) => {
        console.log(err);
        if (!cancelled) setUpcomingBookings([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, memberClientId]);

  // Sorted by created_at (when the cycle was assigned/requested), not start_date — a cycle
  // nobody's booked into yet (every pending request included) has no start_date at all, and
  // `moment(null)` quietly resolves to "right now" rather than an invalid date, which would
  // otherwise sort every such cycle as if it were the most recent.
  const sorted = useMemo(
    () => [...(packages || [])].sort((a, b) => moment(b.created_at).diff(moment(a.created_at))),
    [packages]
  );
  const pendingCycle = useMemo(() => sorted.find((p) => cycleStats(p).isPending) || null, [sorted]);
  // The summary is three exhaustive, mutually-exclusive buckets (plus the pending request,
  // surfaced separately above them — see summaryBody) instead of "current package(s) + a
  // separate flat history list" — "Trenutni" (approved — the trainer already reviewed and OK'd
  // it, so it's the member's live package the moment that happens, not once they've also
  // gotten around to booking a first session against it) and "Prošli" (rejected, used up, or
  // past its own end date). Together with pendingCycle these cover every row `sorted` ever
  // held, so the old dedicated "Istorija paketa" list is gone — there's nothing left it would
  // show that isn't already visible right here.
  //
  // "Budući" (its own bucket for an approved package with no bookings yet) used to exist here
  // — every approved package starts out exactly that way (start_date only exists once there's
  // a real first booking), so it sat in "Budući" until the member's actual first session, one
  // extra manual-feeling step after approval before it read as "their real, current package"
  // anywhere in the app. Folded into `currentCycles` below instead; `futureCycles` is kept
  // (always empty) purely so `hasAnyCycles`/the JSX below don't need touching if a genuine
  // "approved but scheduled to start later" case is ever reintroduced.
  const futureCycles = useMemo(() => [], []);
  const currentCycles = useMemo(
    () => sorted.filter((p) => cycleStats(p).isActive),
    [sorted]
  );
  const pastCycles = useMemo(
    () =>
      sorted.filter((p) => {
        const s = cycleStats(p);
        return s.isRejected || s.isUsedUp || s.isExpiredByDate;
      }),
    [sorted]
  );
  const hasAnyCycles = !!pendingCycle || futureCycles.length > 0 || currentCycles.length > 0 || pastCycles.length > 0;

  useEffect(() => {
    if (view !== "history-detail" || !selectedCycleId) {
      setCycleSessions([]);
      return undefined;
    }
    let cancelled = false;
    setLoadingSessions(true);
    ApiMethods.get(`client-packages/${selectedCycleId}/bookings`)
      .then((res) => {
        if (!cancelled) setCycleSessions(res || []);
      })
      .catch((err) => {
        console.log(err);
        if (!cancelled) setCycleSessions([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingSessions(false);
      });
    return () => {
      cancelled = true;
    };
  }, [view, selectedCycleId]);

  if (!open) return null;

  const memberName = `${member?.first_name || ""} ${member?.last_name || ""}`.trim();
  const memberInitials =
    `${(member?.first_name || "")[0] || ""}${(member?.last_name || "")[0] || ""}`.toUpperCase();
  const selectedCycle = sorted.find((p) => p.id === selectedCycleId) || null;

  // Only meaningful for the "opened straight onto one specific package" entry point
  // (`initialCycleId` — e.g. "Zakazani članovi"'s package icon, which picks the first unpaid
  // active cycle for a member with several) — manually navigating here via a summary row's
  // own "Vidi detalje" (`initialCycleId == null`) never shows tabs, same as today. Tabs
  // themselves only appear once there's genuinely more than one relevant cycle to choose
  // between; a single one keeps today's plain (tab-less) card.
  const relevantCycles =
    initialCycleId != null
      ? sorted.filter((p) => statusKeyFor(p) === "active" && !p.payment_date)
      : [];
  const showCycleTabs = view === "history-detail" && relevantCycles.length > 1;

  const openCycleDetail = (pkg) => {
    setSelectedCycleId(pkg.id);
    setView("history-detail");
    setSessionsExpanded(false);
  };

  // Nije plaćeno/Plaćeno pills (PackageStatusCard) — saves immediately, independent of
  // Approve/Reject, and for any package regardless of status (a trainer can mark an
  // already-approved package paid later, right from this same card). `price`, when given
  // (only from the "Plaćeno" price-confirm popup below), overrides the package's stored price
  // alongside marking it paid; omitted for "Nije plaćeno", which never touches price.
  const handleTogglePaid = async (pkg, nextPaid, price) => {
    if (savingPaidId) return;
    setSavingPaidId(pkg.id);
    try {
      const res = await ApiMethods.post(`client-packages/${pkg.id}/update`, {
        package_id: pkg.package_id,
        payment_date: nextPaid ? moment().format("YYYY-MM-DD") : null,
        price: nextPaid && price != null ? price : undefined,
      });
      if (res?.clientPackage) {
        upsertClientPackageInCache(memberClientId, res.clientPackage);
        onPaymentToggled?.(res.clientPackage, nextPaid);
      }
    } catch (err) {
      console.log(err);
      showAlert("Greška prilikom izmene statusa plaćanja", "error");
    } finally {
      setSavingPaidId(null);
    }
  };

  // "Plaćeno" (PackageStatusCard) routes here instead of straight to handleTogglePaid — opens
  // the price-confirm popup first; "Nije plaćeno" (nextPaid === false) still saves immediately,
  // unchanged.
  const handleTogglePaidRequest = (pkg, nextPaid) => {
    if (!nextPaid) {
      handleTogglePaid(pkg, false);
      return;
    }
    setPayConfirmPrice(String(pkg.price ?? pkg.package_price ?? 0));
    setPayConfirmTarget(pkg);
  };

  // Real (soft) delete of an already-approved package, via the history-detail card's "⋮" menu
  // — see ClientPackageController.deleteClientPackage. Distinct from handleRejectRequest below,
  // which only ever applies to a still-pending request and never touches deleted_at.
  const handleDeletePackage = async () => {
    if (!deleteTarget || deletingPackage) return;
    setDeletingPackage(true);
    try {
      await ApiMethods.delete(`client-packages/${deleteTarget.id}`);
      removeClientPackageFromCache(memberClientId, deleteTarget.id);
      showAlert("Paket je obrisan", "success");
      setDeleteTarget(null);
      // The card just deleted was whatever history-detail was showing — back out to the
      // summary list rather than leaving the view pointed at a cycle that no longer exists.
      setView("summary");
      setSelectedCycleId(null);
    } catch (err) {
      console.log(err);
      showAlert("Greška prilikom brisanja paketa", "error");
    } finally {
      setDeletingPackage(false);
    }
  };

  // "Odobri paket"/"Obriši paket" — for a pending request, right on the summary. Neither is a
  // real delete — see ClientPackageController.approveClientPackage/rejectClientPackage — so
  // the response's updated row is just merged into the cache like any other package edit.
  const handleApproveRequest = async (pkg) => {
    setReviewSaving(true);
    try {
      // Payment isn't decided at this point — PackageStatusCard hides "Status plaćanja"
      // entirely while pending, so this is always null here; staff marks it paid afterward,
      // once it's showing as an actual Budući/Trenutni package. `?? null`, not `|| undefined`
      // — the approve endpoint would otherwise leave the column at whatever it already was
      // instead of the explicit "not decided yet" this state actually is.
      const res = await ApiMethods.post(`client-packages/${pkg.id}/approve`, {
        payment_date: pkg.payment_date ?? null,
      });
      if (res?.clientPackage) upsertClientPackageInCache(memberClientId, res.clientPackage);
      showAlert("Paket je odobren", "success");
      setView("summary");
    } catch (err) {
      console.log(err);
      showAlert("Greška prilikom odobravanja paketa", "error");
    } finally {
      setReviewSaving(false);
    }
  };

  const handleRejectRequest = async (pkg) => {
    setReviewSaving(true);
    try {
      const res = await ApiMethods.post(`client-packages/${pkg.id}/reject`, {});
      if (res?.clientPackage) upsertClientPackageInCache(memberClientId, res.clientPackage);
      showAlert("Zahtev za paket je odbijen", "success");
      setView("summary");
    } catch (err) {
      console.log(err);
      showAlert("Greška prilikom odbijanja zahteva", "error");
    } finally {
      setReviewSaving(false);
    }
  };

  const summaryBody = (
    <>
      {/* Hidden while a request is already awaiting review — a client can only ever have one
          pending request at a time (createClientPackage overwrites the existing one rather
          than piling up a second), so starting a new manual assignment here would either be
          redundant or silently replace the one staff hasn't reviewed yet. Resolve/approve or
          reject it first. */}
      {!pendingCycle && (
        <button type="button" className="hf-pkg-btn-primary hf-pkg-add-btn" onClick={onAssignNew}>
          <AddIcon fontSize="small" />
          Dodaj paket
        </button>
      )}

      {/* Surfaced above the current-package card(s) regardless of whether one of those exists —
          a pending request needs staff attention now, not once they've scrolled past whatever
          else this member has. There's never more than one at a time (see createClientPackage's
          "overwrite the existing pending request" rule). No section label above it (unlike
          Budući/Trenutni/Prošli below) — the modal's own header already reads "Zahtev za
          paket" whenever this card is present, so repeating that immediately above it here
          would just be the same words twice.
          No onClick/"Vidi detalje" either — Odbij/Odobri right here ARE the review screen now;
          the card used to just open a second, near-identical-looking popup with those same two
          actions below it, one extra click for no new information. */}
      {pendingCycle && (
        <div className="hf-pkg-request-group">
          <PackageStatusCard pkg={pendingCycle} />

          <div className="hf-pkg-footer">
            <button
              type="button"
              className="hf-pkg-btn-cancel"
              disabled={reviewSaving}
              onClick={() => handleRejectRequest(pendingCycle)}
            >
              Odbij paket
            </button>
            <button
              type="button"
              className="hf-pkg-btn-primary"
              disabled={reviewSaving}
              onClick={() => handleApproveRequest(pendingCycle)}
            >
              {reviewSaving ? <span className="hf-btn-spinner" /> : "Odobri paket"}
            </button>
          </div>
        </div>
      )}

      {!hasAnyCycles && (
        <div className="hf-pkg-cyc-list">
          <PackageStatusCard placeholder />
        </div>
      )}

      <PackageSection
        title="Budući paketi"
        description="Odobreni paketi koji još nisu počeli."
        cycles={futureCycles}
        onOpen={openCycleDetail}
      />
      <PackageSection
        title="Trenutni paketi"
        description="Aktivni paketi koje koristite."
        cycles={currentCycles}
        onOpen={openCycleDetail}
      />
      <PackageSection
        title="Prošli paketi"
        description="Istekli i završeni paketi."
        cycles={pastCycles}
        onOpen={openCycleDetail}
      />

      {upcomingBookings.length > 0 && (
        <>
          <div className="hf-pkg-history-title">Predstojeći termini</div>
          {upcomingBookings.map((booking) => (
            <div className="hf-pkg-upcoming-row" key={booking.id}>
              <EventAvailableOutlinedIcon sx={{ fontSize: 16 }} />
              <span>{moment(booking.start).format("DD.MM.YYYY")}</span>
              <span className="hf-pkg-upcoming-time">{moment(booking.start).format("HH:mm")}</span>
            </div>
          ))}
        </>
      )}
    </>
  );

  // A pending request never reaches this view anymore — its own note/Odbij/Odobri actions now
  // live directly on the summary screen (see summaryBody above), so nothing ever calls
  // openCycleDetail with one. Every cycle that lands here (via a bucket row's "Vidi detalje")
  // is a real, already-decided one, with real bookings/progress to show.
  //
  // Lets a trainer switch between several relevant cycles for the same member (e.g. two
  // active-but-unpaid packages) without leaving this view — only rendered at all when
  // `showCycleTabs` is true (see its own comment above `relevantCycles`).
  const cycleTabs = showCycleTabs && (
    <div className="hf-pkg-cycle-tabs">
      {relevantCycles.map((p) => (
        <button
          key={p.id}
          type="button"
          className={`hf-pkg-cycle-tab${p.id === selectedCycleId ? " active" : ""}`}
          onClick={() => setSelectedCycleId(p.id)}
        >
          {p.total_sessions ?? p.package_service_qty ?? "—"} termina
        </button>
      ))}
    </div>
  );

  // Split into a "pinned" part (the card itself — icon/name/badge, progress, dates, price/
  // total/remaining, payment) and a "scrollable" part (just the "Odrađeni termini" list) —
  // a long session history shouldn't push the card itself out of view, so only the list
  // scrolls underneath it, same as the header above it.
  const historyDetailPinned = selectedCycle && (
    <PackageStatusCard
      pkg={selectedCycle}
      onTogglePaid={handleTogglePaidRequest}
      saving={savingPaidId === selectedCycle.id}
      showProgress
      showDates
      onDeleteRequest={setDeleteTarget}
    />
  );

  // Collapsed by default to just a summary line (used/remaining count) — "Prikaži sve" expands
  // it into the full table below, and toggles back to just the summary on a second click.
  const historyDetailScrollable = selectedCycle && (() => {
    const { used, remaining } = cycleStats(selectedCycle);
    return (
      <>
        <button
          type="button"
          className="hf-pkg-history-summary"
          onClick={() => setSessionsExpanded((v) => !v)}
        >
          <span className="hf-pkg-history-summary-icon">
            <AccessTimeOutlinedIcon sx={{ fontSize: 18 }} />
          </span>
          <span className="hf-pkg-history-summary-text">
            <span className="hf-pkg-history-title">Istorija termina</span>
            <span className="hf-pkg-history-summary-sub">
              {used} iskorišćenih termina · {remaining} preostalo
            </span>
          </span>
          <span className="hf-pkg-history-summary-action">
            {sessionsExpanded ? "Sakrij" : "Prikaži sve"}
            <ChevronRightIcon
              sx={{ fontSize: 16 }}
              className={`hf-pkg-history-chevron${sessionsExpanded ? " hf-pkg-history-chevron-open" : ""}`}
            />
          </span>
        </button>

        {sessionsExpanded &&
          (loadingSessions ? (
            <div className="hf-pkg-loading">Učitavanje...</div>
          ) : cycleSessions.length === 0 ? (
            <div className="hf-pkg-empty">Nema evidentiranih termina za ovaj paket.</div>
          ) : (
            <div className="hf-pkg-session-table-wrap">
              <table className="hf-pkg-session-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Datum</th>
                    <th>Vreme</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {cycleSessions.map((session, i) => (
                    <SessionTableRow key={session.id} index={i + 1} session={session} />
                  ))}
                </tbody>
              </table>
            </div>
          ))}
      </>
    );
  })();

  const body = view === "summary" ? summaryBody : historyDetailScrollable;
  // A Fragment (not a wrapping div) — its children still render as direct children of the
  // flex-column modal/sheet below, so packageFlow.css's `> .hf-pkg-status-card` selector (and
  // the new `> .hf-pkg-cycle-tabs` one alongside it) keep matching exactly as if this were a
  // single element.
  const pinnedExtra =
    view === "history-detail" ? (
      <>
        {cycleTabs}
        {historyDetailPinned}
      </>
    ) : null;

  const handleClose = () => {
    onClose();
  };

  // "Paket člana" reads as redundant once the whole reason this popup is open is a pending
  // request — the title says so directly instead ("Zahtev za paket"). "history-detail" never
  // shows a pending request anymore (its own review now lives right here on the summary), so
  // that view is always "Detalji paketa". Title + member name always render on one line now
  // (not title/subtitle stacked) — same "Naslov: Ime" format everywhere, not just pending.
  const headerTitle = view === "summary" ? (pendingCycle ? "Zahtev za paket" : "Paket člana") : "Detalji paketa";

  const headerRow = (
    <div className="hf-pkg-header">
      <div className="hf-pkg-header-row">
        {/* Opened directly on a specific package (PackageManagement's "Pregledaj paket",
            via initialCycleId) has no summary underneath it to go "back" to — there was
            never a Budući/Trenutni/Prošli list on the way here, so the button is hidden
            for that entry point rather than going somewhere that skips right past it. */}
        {view !== "summary" && initialCycleId == null && (
          <button
            type="button"
            className="hf-pkg-back"
            aria-label="Nazad"
            onClick={() => setView("summary")}
          >
            <ArrowBackIosNewIcon sx={{ fontSize: 14 }} />
          </button>
        )}
        <h2 className="hf-pkg-title hf-pkg-title-detail">
          {headerTitle}
          {memberName && (
            <>
              {": "}
              <ClientAvatar
                photoUrl={member?.photo_url}
                initials={memberInitials}
                background={colorForName(memberName)}
                wrapperClassName="hf-pkg-title-avatar"
                imgClassName="hf-pkg-title-avatar-img"
              />
              <span className="hf-pkg-title-name">{memberName}</span>
            </>
          )}
        </h2>
      </div>
      <button type="button" className="hf-pkg-close" onClick={handleClose} aria-label="Zatvori">
        <CloseIcon sx={{ fontSize: 16 }} />
      </button>
    </div>
  );

  // Rendered as a sibling of the modal/sheet below (its own `.hf-del-overlay` is `position:
  // fixed`, so DOM placement doesn't matter) — shared by both the Desktop/Tablet and Mobile
  // return branches rather than duplicated in each.
  const deleteConfirmPopup = deleteTarget && (
    <DeletePopUp
      message="Da li ste sigurni da želite da obrišete ovaj paket?"
      subMessage="Svi termini koji su možda zakazani u okviru ovog paketa će biti automatski uklonjeni."
      confirmLabel={deletingPackage ? "Brisanje..." : "Da, želim"}
      onConfirm={handleDeletePackage}
      closePopUp={() => !deletingPackage && setDeleteTarget(null)}
    />
  );

  // "Plaćeno" price-confirm — same shape/classes as PackageAssignFlow's own "Potvrdite cenu"
  // popup (packageFlow.css already styles .hf-pkg-overlay-stacked/.hf-pkg-modal-confirm for
  // both), stacked over this modal instead of over the assign flow. Closes immediately on
  // confirm; the underlying card's own Plaćeno/Nije plaćeno buttons (via `saving` further
  // below, driven by the same savingPaidId) already show the in-flight state while the save
  // itself completes.
  const payConfirmPopup = payConfirmTarget && (
    <div className="hf-pkg-overlay hf-pkg-overlay-stacked">
      <div className="hf-pkg-modal hf-pkg-modal-confirm">
        <div className="hf-pkg-header">
          <div className="hf-pkg-header-row">
            <h2 className="hf-pkg-title">Potvrdite cenu</h2>
          </div>
          <button
            type="button"
            className="hf-pkg-close"
            onClick={() => setPayConfirmTarget(null)}
            aria-label="Zatvori"
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </button>
        </div>
        <div className="hf-pkg-field">
          <label htmlFor="pkg-pay-confirm-price">Cena (RSD)</label>
          <input
            id="pkg-pay-confirm-price"
            className="hf-pkg-input"
            type="number"
            min="0"
            value={payConfirmPrice}
            onChange={(e) => setPayConfirmPrice(e.target.value)}
            autoFocus
          />
        </div>
        <div className="hf-pkg-footer">
          <button
            type="button"
            className="hf-pkg-btn-cancel"
            onClick={() => setPayConfirmTarget(null)}
          >
            Otkaži
          </button>
          <button
            type="button"
            className="hf-pkg-btn-primary"
            disabled={payConfirmPrice === ""}
            onClick={() => {
              const pkg = payConfirmTarget;
              const price = Number(payConfirmPrice);
              setPayConfirmTarget(null);
              handleTogglePaid(pkg, true, price);
            }}
          >
            Potvrdi plaćanje
          </button>
        </div>
      </div>
    </div>
  );

  if (isDesktopTablet) {
    // Both the bucketed summary rows (icon+info, ring, payment, "Vidi detalje") and the
    // history-detail view (card + Počinje/Ističe + stat tiles + session list) share this same
    // wider-than-.hf-pkg-modal's-plain-480px width now — .hf-pkg-modal-summary-wide applies
    // regardless of `view` so switching between them (via "Vidi detalje"/"Nazad") doesn't
    // resize the popup out from under the user.
    return (
      <div className="hf-pkg-overlay">
        <div className="hf-pkg-modal hf-pkg-modal-details hf-pkg-modal-summary-wide">
          {headerRow}
          {pinnedExtra}
          <div className="hf-pkg-scroll-body">{body}</div>
        </div>
        {deleteConfirmPopup}
        {payConfirmPopup}
      </div>
    );
  }

  return (
    <div className="hf-pkg-overlay">
      <div className="hf-pkg-sheet hf-pkg-sheet-scroll hf-pkg-sheet-details">
        <div className="hf-pkg-sheet-handle" />
        {headerRow}
        {pinnedExtra}
        <div className="hf-pkg-scroll-body">{body}</div>
      </div>
      {deleteConfirmPopup}
      {payConfirmPopup}
    </div>
  );
};

export default PackageDetailsModal;
